/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 26-07-2026
 *
 * De projectvlaggen die de ng21-engines nodig hebben (stap 8). Sinds golf a1 komen ze
 * rechtstreeks uit de `SessieService`: die bezit de userdata, dus de push vanuit AngularJS
 * (`initialiseer`) is vervallen. De service blijft bestaan omdat de engines en de eilanden op
 * deze Nederlandse namen leunen en niet op de kolomnamen van het project.
 */
import {computed, inject, Injectable} from '@angular/core';
import {SessieService} from './sessie.service';

@Injectable({providedIn: 'root'})
export class ProjectVlaggenService {

    /**
     * De sessie: bron van de projectgegevens waar deze vlaggen uit komen.
     */
    private readonly sessie = inject(SessieService);

    /**
     * Projectvlag: feedbackloop-simfeedback beschikbaar in dit project?
     */
    readonly feedbFeedbackloop = computed(() => !!this.sessie.projectdata()?.feedb_feedbackloop);

    /**
     * Projectvlag: beginwaarden-simfeedback beschikbaar in dit project?
     */
    readonly feedbBeginwaarden = computed(() => !!this.sessie.projectdata()?.feedb_beginwaarden);

    /**
     * Projectvlag: toont het project de normfoutfeedback (het vraagteken in de feedbackbalk)?
     */
    readonly normFoutfeedback = computed(() => !!this.sessie.projectdata()?.norm_foutfeedback);

    /**
     * Projectvlag: worden normfouten op het canvas gehighlight? Staat los van `normFoutfeedback`.
     */
    readonly normFoutfeedbackHighlight = computed(() => !!this.sessie.projectdata()?.norm_foutfeedback_highlight);

    /**
     * Projectvlag: kent dit project noodzakelijke bouwsuggesties?
     */
    readonly bouwsuggestiesNoodzakelijk = computed(() => !!this.sessie.projectdata()?.bouwsuggesties_noodzakelijk);

    /**
     * Projectvlag: kent dit project gewenste bouwsuggesties?
     */
    readonly bouwsuggestiesGewenst = computed(() => !!this.sessie.projectdata()?.bouwsuggesties_gewenst);

    /**
     * Projectvlag: kent dit project bouwsuggesties, van welke soort dan ook?
     */
    readonly bouwsuggesties = computed(() => this.bouwsuggestiesNoodzakelijk() || this.bouwsuggestiesGewenst());

    /**
     * Projectvlag: is de tekstbeschrijving beschikbaar in dit project?
     */
    readonly tekstbeschrijving = computed(() => !!this.sessie.projectdata()?.tekstbeschrijving);

    /**
     * Projectvlag: staat de normbalk boven het canvas in dit project? (golf 4e; de ng1-modelview las
     * hem als `normbalk()` uit de userdata)
     */
    readonly normbalk = computed(() => !!this.sessie.projectdata()?.norm_balk);

    /**
     * Projectvlag: kent dit project de beginnersmodus? (golf 4e; idem)
     */
    readonly beginnersmodus = computed(() => !!this.sessie.projectdata()?.beginnersmodus);

    /**
     * Projectvlag: toont het canvas bij het zweven boven een node uitleg over dat ingrediënt?
     */
    readonly tooltipIngredient = computed(() => !!this.sessie.projectdata()?.tooltip_ingredient);

    /**
     * Projectvlag: toont het nodemenu uitleg bij zijn items (de bouwactiviteiten)?
     */
    readonly tooltipBouwactiviteit = computed(() => !!this.sessie.projectdata()?.tooltip_bouwactiviteit);
}
